import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Alert,
  CircularProgress,
  Slider,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControlLabel,
  Checkbox,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Timeline as TimelineIcon,
  AccountTree as AccountTreeIcon,
  GridOn as GridOnIcon,
  Palette as PaletteIcon,
  MovieFilter as MovieFilterIcon,
  Widgets as WidgetsIcon,
  Description as DescriptionIcon,
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  PlayArrow as PlayArrowIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  DesignServices as DesignIcon
} from '@mui/icons-material';
import { UXDesignService } from '../services/uxDesignService';
import {
  StartUXDesignSessionRequest,
  UXDesignSession,
  ArtifactType,
  UXArtifact,
  PersonaArtifact,
  UserJourneyArtifact,
  UserFlowArtifact,
  WireframeArtifact,
  MockupArtifact,
  StoryboardArtifact,
  PrototypeArtifact,
  DesignSystemArtifact,
  UsabilityReportArtifact
} from '../models/uxDesign';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ux-tabpanel-${index}`}
      aria-labelledby={`ux-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

const artifactIcons: Record<ArtifactType, React.ReactElement> = {
  persona: <PersonIcon />,
  userJourney: <TimelineIcon />,
  userFlow: <AccountTreeIcon />,
  wireframe: <GridOnIcon />,
  mockup: <PaletteIcon />,
  storyboard: <MovieFilterIcon />,
  prototype: <WidgetsIcon />,
  designSystem: <DescriptionIcon />,
  usabilityReport: <AssessmentIcon />
};

export const UXDesigner: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Session state
  const [sessions, setSessions] = useState<UXDesignSession[]>([]);
  const [currentSession, setCurrentSession] = useState<UXDesignSession | null>(null);

  // New session form state
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [targetPlatform, setTargetPlatform] = useState<'web' | 'mobile' | 'desktop' | 'all'>('web');
  const [designSystem, setDesignSystem] = useState<'material' | 'apple' | 'custom' | undefined>('material');
  const [targetAudience, setTargetAudience] = useState('');
  const [additionalContext, setAdditionalContext] = useState<Record<string, string>>({
    focus: '',
    features: '',
    style: '',
    other: ''
  });
  const [businessGoals, setBusinessGoals] = useState('');
  const [constraints, setConstraints] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(1000);

  // Artifact generation state
  const [selectedArtifacts, setSelectedArtifacts] = useState<ArtifactType[]>([]);
  const [specificRequirements, setSpecificRequirements] = useState('');
  const [fidelity, setFidelity] = useState<'low' | 'medium' | 'high'>('medium');
  const [activeStep, setActiveStep] = useState(0);

  // Load sessions on mount
  useEffect(() => {
    const localSessions = UXDesignService.getAllLocalSessions();
    setSessions(localSessions);
  }, []);

  const handleStartSession = async () => {
    if (!projectName || !projectDescription || !targetAudience) {
      setError('Please provide project name, description, and target audience');
      return;
    }

    // Filter out empty values from additionalContext
    const filteredContext = Object.entries(additionalContext).reduce((acc, [key, value]) => {
      if (value.trim()) {
        acc[key] = value.trim();
      }
      return acc;
    }, {} as Record<string, string>);

    if (Object.keys(filteredContext).length === 0) {
      setError('Please provide at least one additional context field');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const request: StartUXDesignSessionRequest = {
        projectName,
        projectDescription,
        targetPlatform,
        designSystem,
        targetAudience,
        additionalContext: filteredContext,
        businessGoals: businessGoals || undefined,
        constraints: constraints || undefined,
        temperature,
        maxTokens
      };

      const response = await UXDesignService.startSession(request);
      
      // Create initial session object
      const newSession: UXDesignSession = {
        sessionId: response.sessionId,
        projectName: response.projectName,
        projectDescription,
        targetPlatform,
        designSystem,
        artifacts: [],
        createdAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString()
      };

      // Save to local storage
      UXDesignService.saveSessionToLocal(newSession);
      
      // Update state
      setSessions([...sessions, newSession]);
      setCurrentSession(newSession);
      setActiveTab(1);
      setSuccess('UX design session started successfully!');
      
      // Reset form
      setProjectName('');
      setProjectDescription('');
      setTargetAudience('');
      setAdditionalContext({
        focus: '',
        features: '',
        style: '',
        other: ''
      });
      setBusinessGoals('');
      setConstraints('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to start session');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateArtifacts = async () => {
    if (!currentSession || selectedArtifacts.length === 0) {
      setError('Please select at least one artifact type');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (selectedArtifacts.length === 1) {
        // Single artifact generation
        const response = await UXDesignService.generateArtifact(currentSession.sessionId, {
          artifactType: selectedArtifacts[0],
          specificRequirements: specificRequirements.trim() || `Generate a ${UXDesignService.getArtifactTypeLabel(selectedArtifacts[0])} for ${currentSession.projectName}`,
          fidelity,
          temperature,
          maxTokens
        });

        // Update session with new artifact
        console.log('Generated artifact response:', response); // Debug log
        console.log('Artifact structure:', response.artifact); // Debug log
        
        const newArtifact = response.artifact;
        if (!newArtifact || !newArtifact.id) {
          console.error('Invalid artifact response:', newArtifact);
          setError('Received invalid artifact from server');
          return;
        }
        
        const updatedSession = {
          ...currentSession,
          artifacts: [...currentSession.artifacts, newArtifact],
          lastUpdated: new Date().toISOString()
        };

        UXDesignService.saveSessionToLocal(updatedSession);
        setCurrentSession(updatedSession);
        setSessions(sessions.map(s => s.sessionId === currentSession.sessionId ? updatedSession : s));
        setSuccess(`${UXDesignService.getArtifactTypeLabel(selectedArtifacts[0])} generated successfully!`);
      } else {
        // Bulk generation
        const response = await UXDesignService.bulkGenerateArtifacts(currentSession.sessionId, {
          artifactTypes: selectedArtifacts,
          specificRequirements: specificRequirements.trim() || `Generate artifacts for ${currentSession.projectName}`,
          fidelity,
          temperature,
          maxTokens
        });

        // Update session with new artifacts
        console.log('Bulk generated artifacts response:', response); // Debug log
        console.log('Artifacts array:', response.artifacts); // Debug log
        
        const newArtifacts = response.artifacts || [];
        if (!Array.isArray(newArtifacts) || newArtifacts.some(artifact => !artifact || !artifact.id)) {
          console.error('Invalid artifacts response:', newArtifacts);
          setError('Received invalid artifacts from server');
          return;
        }
        
        const updatedSession = {
          ...currentSession,
          artifacts: [...currentSession.artifacts, ...newArtifacts.filter(artifact => artifact && artifact.id)],
          lastUpdated: new Date().toISOString()
        };

        UXDesignService.saveSessionToLocal(updatedSession);
        setCurrentSession(updatedSession);
        setSessions(sessions.map(s => s.sessionId === currentSession.sessionId ? updatedSession : s));
        setSuccess(`${response.artifacts.length} artifacts generated successfully!`);
      }

      // Clear selection
      setSelectedArtifacts([]);
      setSpecificRequirements('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate artifacts');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadSession = (session: UXDesignSession) => {
    setCurrentSession(session);
    setActiveTab(1);
  };

  const handleExportSession = () => {
    if (currentSession) {
      UXDesignService.downloadSession(currentSession);
      setSuccess('Session exported successfully!');
    }
  };

  const handleImportSession = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const imported = UXDesignService.importSession(e.target?.result as string);
          UXDesignService.saveSessionToLocal(imported);
          setSessions([...sessions, imported]);
          setCurrentSession(imported);
          setActiveTab(1);
          setSuccess('Session imported successfully!');
        } catch (err) {
          setError('Failed to import session. Invalid file format.');
        }
      };
      reader.readAsText(file);
    }
  };

  const handleDeleteSession = (sessionId: string) => {
    UXDesignService.deleteLocalSession(sessionId);
    setSessions(sessions.filter(s => s.sessionId !== sessionId));
    if (currentSession?.sessionId === sessionId) {
      setCurrentSession(null);
      setActiveTab(0);
    }
    setSuccess('Session deleted successfully');
  };

  const renderArtifactContent = (artifact: UXArtifact) => {
    switch (artifact.type) {
      case 'persona':
        return <PersonaView persona={artifact.content as PersonaArtifact} />;
      case 'userJourney':
        return <UserJourneyView journey={artifact.content as UserJourneyArtifact} />;
      case 'userFlow':
        return <UserFlowView flow={artifact.content as UserFlowArtifact} />;
      case 'wireframe':
        return <WireframeView wireframe={artifact.content as WireframeArtifact} />;
      case 'mockup':
        return <MockupView mockup={artifact.content as MockupArtifact} />;
      case 'storyboard':
        return <StoryboardView storyboard={artifact.content as StoryboardArtifact} />;
      case 'prototype':
        return <PrototypeView prototype={artifact.content as PrototypeArtifact} />;
      case 'designSystem':
        return <DesignSystemView designSystem={artifact.content as DesignSystemArtifact} />;
      case 'usabilityReport':
        return <UsabilityReportView report={artifact.content as UsabilityReportArtifact} />;
      default:
        return <pre>{JSON.stringify(artifact.content, null, 2)}</pre>;
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: 1, 
            mb: 2,
            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' }
          }}
        >
          <DesignIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          {isMobile ? 'UX Designer' : 'UI/UX Designer Agent'}
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {isMobile ? 'Create UX design artifacts with AI' : 'Create comprehensive UX design artifacts using AI-powered generation'}
        </Typography>
      </Box>

      <Paper 
        elevation={0}
        sx={{ 
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 3 } }}>

        {error && (
          <Alert 
            severity="error" 
            onClose={() => setError(null)} 
            sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert 
            severity="success" 
            onClose={() => setSuccess(null)} 
            sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            {success}
          </Alert>
        )}

        <Tabs 
          value={activeTab} 
          onChange={(_, v) => setActiveTab(v)}
          sx={{ mb: 2 }}
          variant={isMobile ? "fullWidth" : "standard"}
        >
          <Tab 
            label={isMobile ? "New" : "New Session"}
            sx={{ 
              minHeight: 48,
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }}
          />
          <Tab 
            label={isMobile ? "Current" : "Current Session"}
            disabled={!currentSession}
            sx={{ 
              minHeight: 48,
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }}
          />
          <Tab 
            label={isMobile ? "History" : "Session History"}
            sx={{ 
              minHeight: 48,
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }}
          />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 }, maxWidth: 600, mx: 'auto' }}>
            <Stack spacing={{ xs: 2, sm: 3 }}>
              <TextField
                fullWidth
                label={isMobile ? "Name" : "Project Name"}
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                required
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />
              
              <TextField
                fullWidth
                select
                label="Target Platform"
                value={targetPlatform}
                onChange={(e) => setTargetPlatform(e.target.value as any)}
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              >
                <MenuItem value="web" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Web</MenuItem>
                <MenuItem value="mobile" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Mobile</MenuItem>
                <MenuItem value="desktop" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Desktop</MenuItem>
                <MenuItem value="all" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>All Platforms</MenuItem>
              </TextField>
              
              <TextField
                fullWidth
                multiline
                rows={3}
                label={isMobile ? "Description" : "Project Description"}
                value={projectDescription}
                onChange={(e) => setProjectDescription(e.target.value)}
                required
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />
              
              <TextField
                fullWidth
                select
                label="Design System"
                value={designSystem || ''}
                onChange={(e) => setDesignSystem(e.target.value as any || undefined)}
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              >
                <MenuItem value="" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>None</MenuItem>
                <MenuItem value="material" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Material Design</MenuItem>
                <MenuItem value="apple" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Apple HIG</MenuItem>
                <MenuItem value="custom" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Custom</MenuItem>
              </TextField>
              
              <Box>
                <Typography 
                  gutterBottom
                  sx={{ 
                    fontSize: { xs: '0.875rem', sm: '0.95rem' },
                    fontWeight: 600
                  }}
                >
                  Temperature: {temperature}
                </Typography>
                <Slider
                  value={temperature}
                  onChange={(_, v) => setTemperature(v as number)}
                  min={0}
                  max={1}
                  step={0.1}
                  marks
                />
              </Box>
              
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Target Audience"
                value={targetAudience}
                onChange={(e) => setTargetAudience(e.target.value)}
                helperText={isMobile ? "Target users" : "Describe your target users and their needs"}
                required
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                FormHelperTextProps={{
                  sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                }}
              />
              
              <Typography 
                variant="subtitle2" 
                gutterBottom
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                {isMobile ? 'Context (provide one)' : 'Additional Context (provide at least one)'}
              </Typography>
              
              <TextField
                fullWidth
                label="Primary Focus"
                value={additionalContext.focus}
                onChange={(e) => setAdditionalContext({...additionalContext, focus: e.target.value})}
                helperText={isMobile ? "Main focus" : "Main focus area (e.g., 'comic book generation')"}
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                FormHelperTextProps={{
                  sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                }}
              />
              
              <TextField
                fullWidth
                label="Key Features"
                value={additionalContext.features}
                onChange={(e) => setAdditionalContext({...additionalContext, features: e.target.value})}
                helperText={isMobile ? "Features" : "Important features to include"}
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                FormHelperTextProps={{
                  sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                }}
              />
              
              <TextField
                fullWidth
                label="Visual Style"
                value={additionalContext.style}
                onChange={(e) => setAdditionalContext({...additionalContext, style: e.target.value})}
                helperText={isMobile ? "Style/theme" : "Preferred visual style or theme"}
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                FormHelperTextProps={{
                  sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                }}
              />
              
              <TextField
                fullWidth
                label={isMobile ? "Other" : "Other Requirements"}
                value={additionalContext.other}
                onChange={(e) => setAdditionalContext({...additionalContext, other: e.target.value})}
                helperText={isMobile ? "Other needs" : "Any other specific requirements"}
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                FormHelperTextProps={{
                  sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                }}
              />
              
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Business Goals (Optional)"
                value={businessGoals}
                onChange={(e) => setBusinessGoals(e.target.value)}
                helperText={isMobile ? "Business objectives" : "What are the key business objectives?"}
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                FormHelperTextProps={{
                  sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                }}
              />
              
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Constraints (Optional)"
                value={constraints}
                onChange={(e) => setConstraints(e.target.value)}
                helperText={isMobile ? "Tech/budget limits" : "Any technical, budget, or timeline constraints?"}
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                FormHelperTextProps={{
                  sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                }}
              />
              
              <Button
                variant="contained"
                size="large"
                onClick={handleStartSession}
                disabled={loading || !projectName || !projectDescription || !targetAudience || 
                  !Object.values(additionalContext).some(v => v.trim())}
                startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <PlayArrowIcon />)}
                sx={{ 
                  mt: 2,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  minHeight: 44
                }}
              >
                {loading ? 'Starting...' : (isMobile ? 'Start Session' : 'Start UX Design Session')}
              </Button>
            </Stack>
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {currentSession && (
            <>
              <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between', 
                  alignItems: { xs: 'flex-start', sm: 'center' }, 
                  mb: 2,
                  gap: { xs: 2, sm: 0 }
                }}>
                  <div>
                    <Typography 
                      variant="h6"
                      sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                    >
                      {currentSession.projectName}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.875rem', sm: '0.95rem' } }}
                    >
                      {isMobile && currentSession.projectDescription.length > 100 
                        ? currentSession.projectDescription.slice(0, 100) + '...'
                        : currentSession.projectDescription
                      }
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Chip 
                        label={currentSession.targetPlatform} 
                        size="small" 
                        sx={{ mr: 1, fontSize: { xs: '0.7rem', sm: '0.75rem' } }} 
                      />
                      {currentSession.designSystem && (
                        <Chip 
                          label={currentSession.designSystem} 
                          size="small"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        />
                      )}
                    </Box>
                  </div>
                  <Button
                    variant="outlined"
                    startIcon={!isMobile && <DownloadIcon />}
                    onClick={handleExportSession}
                    sx={{ 
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      minHeight: 44
                    }}
                  >
                    {isMobile ? 'Export' : 'Export Session'}
                  </Button>
                </Box>
              </Paper>

              {/* UX Design Workflow Steps */}
              <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                {UXDesignService.getRecommendedArtifactOrder().map((flowType, index) => {
                  const flowArtifacts = currentSession.artifacts.filter(artifact => artifact && artifact.type === flowType);
                  const isCompleted = flowArtifacts.length > 0;
                  
                  return (
                    <Accordion key={flowType} sx={{ mb: 2 }}>
                      <AccordionSummary 
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          backgroundColor: isCompleted ? 'success.light' : 'background.default',
                          '&:hover': {
                            backgroundColor: isCompleted ? 'success.main' : 'action.hover',
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                            {isCompleted && <CheckCircleIcon color="success" sx={{ mr: 1, fontSize: { xs: 20, sm: 24 } }} />}
                            <Box sx={{ fontSize: { xs: 20, sm: 24 }, display: 'flex' }}>
                              {artifactIcons[flowType as ArtifactType]}
                            </Box>
                          </Box>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography 
                              variant="h6"
                              sx={{ fontSize: { xs: '0.95rem', sm: '1.125rem' } }}
                            >
                              {isMobile && UXDesignService.getArtifactTypeLabel(flowType).length > 20
                                ? UXDesignService.getArtifactTypeLabel(flowType).split(' ')[0]
                                : UXDesignService.getArtifactTypeLabel(flowType)
                              }
                            </Typography>
                            {!isMobile && (
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ fontSize: { xs: '0.875rem', sm: '0.95rem' } }}
                              >
                                {UXDesignService.getArtifactTypeDescription(flowType)}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                            >
                              {flowArtifacts.length} {isMobile ? '' : `artifact${flowArtifacts.length !== 1 ? 's' : ''}`}
                            </Typography>
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {flowArtifacts.length > 0 ? (
                          <Box>
                            <Typography 
                              variant="subtitle2" 
                              gutterBottom
                              sx={{ fontSize: { xs: '0.875rem', sm: '0.95rem' } }}
                            >
                              {isMobile ? `Generated (${flowArtifacts.length})` : `Generated Artifacts (${flowArtifacts.length})`}
                            </Typography>
                            {flowArtifacts
                              .filter(artifact => artifact && artifact.id && artifact.metadata)
                              .map((artifact, artifactIndex) => (
                              <Accordion key={artifact.id} sx={{ mb: 1 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <Typography 
                                      sx={{ 
                                        flexGrow: 1,
                                        fontSize: { xs: '0.875rem', sm: '1rem' }
                                      }}
                                    >
                                      Version {artifact.metadata?.version || 1}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {artifact.metadata?.createdAt ? new Date(artifact.metadata.createdAt).toLocaleString() : 'Unknown'}
                                    </Typography>
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                  {renderArtifactContent(artifact)}
                                </AccordionDetails>
                              </Accordion>
                            ))}
                          </Box>
                        ) : (
                          <Box>
                            <Alert severity="info" sx={{ mb: 2 }}>
                              No artifacts generated for this step yet.
                            </Alert>
                            
                            {/* Generate Artifact Form */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <TextField
                                fullWidth
                                select
                                label="Fidelity Level"
                                value={fidelity}
                                onChange={(e) => setFidelity(e.target.value as any)}
                              >
                                <MenuItem value="low">Low</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="high">High</MenuItem>
                              </TextField>
                              
                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Specific Requirements (Optional)"
                                value={specificRequirements}
                                onChange={(e) => setSpecificRequirements(e.target.value)}
                              />
                              
                              <Button
                                variant="contained"
                                onClick={() => {
                                  setSelectedArtifacts([flowType as ArtifactType]);
                                  // Ensure specificRequirements has a value before generating
                                  if (!specificRequirements.trim()) {
                                    setSpecificRequirements(`Create a detailed ${UXDesignService.getArtifactTypeLabel(flowType)} for this project`);
                                  }
                                  handleGenerateArtifacts();
                                }}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
                              >
                                {loading ? 'Generating...' : `Generate ${UXDesignService.getArtifactTypeLabel(flowType)}`}
                              </Button>
                            </Box>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            </>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between', 
              alignItems: { xs: 'flex-start', sm: 'center' }, 
              mb: { xs: 2, sm: 3 },
              gap: { xs: 2, sm: 0 }
            }}>
              <Typography 
                variant="h6"
                sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
              >
                {isMobile ? 'History' : 'Session History'}
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={!isMobile && <UploadIcon />}
                sx={{ 
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  minHeight: 44
                }}
              >
                {isMobile ? 'Import' : 'Import Session'}
                <input
                  type="file"
                  hidden
                  accept=".json"
                  onChange={handleImportSession}
                />
              </Button>
            </Box>

            {sessions.length === 0 ? (
              <Alert 
                severity="info"
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                {isMobile ? 'No sessions. Start or import one.' : 'No sessions found. Start a new session or import an existing one.'}
              </Alert>
            ) : (
              <Grid container spacing={{ xs: 2, sm: 2 }}>
                {sessions.map((session) => (
                  <Grid item xs={12} md={6} lg={4} key={session.sessionId}>
                    <Card>
                      <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                        <Typography 
                          variant="h6" 
                          gutterBottom
                          sx={{ fontSize: { xs: '0.95rem', sm: '1.125rem' } }}
                        >
                          {session.projectName}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          paragraph
                          sx={{ fontSize: { xs: '0.875rem', sm: '0.95rem' } }}
                        >
                          {isMobile && session.projectDescription.length > 80
                            ? session.projectDescription.slice(0, 80) + '...'
                            : session.projectDescription
                          }
                        </Typography>
                        <Box sx={{ mb: 1 }}>
                          <Chip 
                            label={session.targetPlatform} 
                            size="small" 
                            sx={{ mr: 1, fontSize: { xs: '0.7rem', sm: '0.75rem' } }} 
                          />
                          {session.designSystem && (
                            <Chip 
                              label={session.designSystem} 
                              size="small"
                              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                            />
                          )}
                        </Box>
                        <Typography 
                          variant="caption" 
                          display="block"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        >
                          Artifacts: {session.artifacts.length}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        >
                          Created: {new Date(session.createdAt).toLocaleDateString()}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <Button 
                          size="small" 
                          onClick={() => handleLoadSession(session)}
                          sx={{ 
                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                            minHeight: 36
                          }}
                        >
                          Open
                        </Button>
                        <Tooltip title={isMobile ? "" : "Delete Session"}>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteSession(session.sessionId)}
                            color="error"
                            sx={{ minWidth: 36, minHeight: 36 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </TabPanel>
      </Box>
      </Paper>
    </Container>
  );
};

// Artifact view components
const PersonaView: React.FC<{ persona: PersonaArtifact }> = ({ persona }) => (
  <Box>
    <Typography variant="h6" gutterBottom>{persona.name}</Typography>
    <Typography variant="body2" fontStyle="italic" paragraph>"{persona.quote}"</Typography>
    
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle2" gutterBottom>Demographics</Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Age" secondary={persona.demographicInfo.age} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Occupation" secondary={persona.demographicInfo.occupation} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Location" secondary={persona.demographicInfo.location} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Education" secondary={persona.demographicInfo.education} />
          </ListItem>
        </List>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle2" gutterBottom>Psychographics</Typography>
        <Typography variant="body2" paragraph>
          Tech Savviness: {persona.psychographicInfo.techSavviness}
        </Typography>
        <Typography variant="body2">
          Personality: {persona.psychographicInfo.personality.join(', ')}
        </Typography>
      </Grid>
      
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>Goals</Typography>
        <List dense>
          {persona.goals.map((goal, i) => (
            <ListItem key={i}>
              <ListItemText primary={goal} />
            </ListItem>
          ))}
        </List>
      </Grid>
      
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>Frustrations</Typography>
        <List dense>
          {persona.frustrations.map((frustration, i) => (
            <ListItem key={i}>
              <ListItemText primary={frustration} />
            </ListItem>
          ))}
        </List>
      </Grid>
    </Grid>
  </Box>
);

const UserJourneyView: React.FC<{ journey: UserJourneyArtifact }> = ({ journey }) => (
  <Box>
    <Typography variant="h6" gutterBottom>{journey.journeyName}</Typography>
    <Typography variant="body2" color="text.secondary" paragraph>
      Persona: {journey.personaName}
    </Typography>
    
    {journey.mermaidDiagram && (
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
        <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
          {journey.mermaidDiagram}
        </Typography>
      </Paper>
    )}
    
    <Typography variant="subtitle2" gutterBottom>Journey Stages</Typography>
    {journey.stages.map((stage, index) => (
      <Accordion key={index}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{stage.name}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">Actions</Typography>
              <List dense>
                {stage.actions.map((action, i) => (
                  <ListItem key={i}>
                    <ListItemText primary={action} />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">Pain Points</Typography>
              <List dense>
                {stage.painPoints.map((pain, i) => (
                  <ListItem key={i}>
                    <ListItemText primary={pain} />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    ))}
  </Box>
);

const UserFlowView: React.FC<{ flow: UserFlowArtifact }> = ({ flow }) => (
  <Box>
    <Typography variant="h6" gutterBottom>{flow.flowName}</Typography>
    <Typography variant="body2" paragraph>{flow.description}</Typography>
    
    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
      <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
        {flow.mermaidDiagram}
      </Typography>
    </Paper>
    
    <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>Flow Steps</Typography>
    <List>
      {flow.steps.map((step) => (
        <ListItem key={step.id}>
          <ListItemIcon>
            <Chip label={step.type} size="small" />
          </ListItemIcon>
          <ListItemText
            primary={step.name}
            secondary={step.description}
          />
        </ListItem>
      ))}
    </List>
  </Box>
);

const WireframeView: React.FC<{ wireframe: WireframeArtifact }> = ({ wireframe }) => (
  <Box>
    <Typography variant="h6" gutterBottom>{wireframe.screenName}</Typography>
    <Typography variant="body2" paragraph>{wireframe.description}</Typography>
    
    {wireframe.mermaidDiagram && (
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
        <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
          {wireframe.mermaidDiagram}
        </Typography>
      </Paper>
    )}
    
    <Typography variant="subtitle2" gutterBottom>Layout</Typography>
    <Typography variant="body2">
      {wireframe.layout.columns} columns × {wireframe.layout.rows} rows, 
      {wireframe.layout.spacing}px spacing, {wireframe.layout.padding}px padding
    </Typography>
    
    <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>Elements</Typography>
    <List dense>
      {wireframe.elements.map((element) => (
        <ListItem key={element.id}>
          <ListItemIcon>
            <Chip label={element.type} size="small" />
          </ListItemIcon>
          <ListItemText primary={element.label} />
        </ListItem>
      ))}
    </List>
    
    {wireframe.annotations.length > 0 && (
      <>
        <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>Annotations</Typography>
        <List dense>
          {wireframe.annotations.map((annotation, i) => (
            <ListItem key={i}>
              <ListItemText primary={annotation} />
            </ListItem>
          ))}
        </List>
      </>
    )}
  </Box>
);

const MockupView: React.FC<{ mockup: MockupArtifact }> = ({ mockup }) => (
  <Box>
    <Typography variant="h6" gutterBottom>{mockup.screenName}</Typography>
    <Typography variant="body2" paragraph>{mockup.description}</Typography>
    
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle2" gutterBottom>Color Scheme</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title="Primary">
            <Box sx={{ width: 40, height: 40, bgcolor: mockup.colorScheme.primary, border: 1, borderColor: 'divider' }} />
          </Tooltip>
          <Tooltip title="Secondary">
            <Box sx={{ width: 40, height: 40, bgcolor: mockup.colorScheme.secondary, border: 1, borderColor: 'divider' }} />
          </Tooltip>
          <Tooltip title="Accent">
            <Box sx={{ width: 40, height: 40, bgcolor: mockup.colorScheme.accent, border: 1, borderColor: 'divider' }} />
          </Tooltip>
          <Tooltip title="Background">
            <Box sx={{ width: 40, height: 40, bgcolor: mockup.colorScheme.background, border: 1, borderColor: 'divider' }} />
          </Tooltip>
        </Box>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle2" gutterBottom>Typography</Typography>
        <Typography variant="body2">Heading: {mockup.typography.headingFont}</Typography>
        <Typography variant="body2">Body: {mockup.typography.bodyFont}</Typography>
      </Grid>
    </Grid>
    
    <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>Design Elements</Typography>
    <List dense>
      {mockup.designElements.map((element) => (
        <ListItem key={element.id}>
          <ListItemText
            primary={element.type}
            secondary={element.content}
          />
        </ListItem>
      ))}
    </List>
  </Box>
);

const StoryboardView: React.FC<{ storyboard: StoryboardArtifact }> = ({ storyboard }) => (
  <Box>
    <Typography variant="h6" gutterBottom>{storyboard.scenarioName}</Typography>
    <Typography variant="body2" paragraph>{storyboard.description}</Typography>
    
    {storyboard.frames.map((frame) => (
      <Card key={frame.frameNumber} sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Frame {frame.frameNumber}: {frame.title}
          </Typography>
          <Typography variant="body2" paragraph>{frame.description}</Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">User Actions</Typography>
              <List dense>
                {frame.userActions.map((action, i) => (
                  <ListItem key={i}>
                    <ListItemText primary={action} />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">System Response</Typography>
              <List dense>
                {frame.systemResponse.map((response, i) => (
                  <ListItem key={i}>
                    <ListItemText primary={response} />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
          
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Emotional State:</strong> {frame.emotionalState}
          </Typography>
        </CardContent>
      </Card>
    ))}
  </Box>
);

const PrototypeView: React.FC<{ prototype: PrototypeArtifact }> = ({ prototype }) => (
  <Box>
    <Typography variant="h6" gutterBottom>{prototype.prototypeName}</Typography>
    <Typography variant="body2" paragraph>{prototype.description}</Typography>
    
    <Typography variant="subtitle2" gutterBottom>Navigation Flow</Typography>
    <Typography variant="body2" paragraph>{prototype.navigationFlow}</Typography>
    
    <Typography variant="subtitle2" gutterBottom>Screens</Typography>
    <List>
      {prototype.screens.map((screen) => (
        <ListItem key={screen.id}>
          <ListItemText primary={screen.name} />
        </ListItem>
      ))}
    </List>
    
    <Typography variant="subtitle2" gutterBottom>Interactions</Typography>
    <List dense>
      {prototype.interactions.map((interaction) => (
        <ListItem key={interaction.id}>
          <ListItemText
            primary={`${interaction.trigger} → ${interaction.action}`}
            secondary={`Target: ${interaction.targetScreen}`}
          />
        </ListItem>
      ))}
    </List>
  </Box>
);

const DesignSystemView: React.FC<{ designSystem: DesignSystemArtifact }> = ({ designSystem }) => (
  <Box>
    <Typography variant="h6" gutterBottom>{designSystem.systemName}</Typography>
    
    <Typography variant="subtitle2" gutterBottom>Design Principles</Typography>
    <List dense>
      {designSystem.principles.map((principle, i) => (
        <ListItem key={i}>
          <ListItemText primary={principle} />
        </ListItem>
      ))}
    </List>
    
    <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>Components</Typography>
    {designSystem.components.map((component, i) => (
      <Accordion key={i}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{component.name}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>{component.description}</Typography>
          <Typography variant="body2">
            <strong>Variants:</strong> {component.variants.join(', ')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Usage:</strong> {component.usage}
          </Typography>
        </AccordionDetails>
      </Accordion>
    ))}
    
    <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>Design Tokens</Typography>
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Typography variant="body2" gutterBottom>Colors</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {Object.entries(designSystem.tokens.colors).map(([name, color]) => (
            <Tooltip key={name} title={name}>
              <Box sx={{ width: 30, height: 30, bgcolor: color, border: 1, borderColor: 'divider' }} />
            </Tooltip>
          ))}
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Typography variant="body2" gutterBottom>Spacing</Typography>
        <Typography variant="caption">
          {Object.entries(designSystem.tokens.spacing).map(([k, v]) => `${k}: ${v}`).join(', ')}
        </Typography>
      </Grid>
    </Grid>
  </Box>
);

const UsabilityReportView: React.FC<{ report: UsabilityReportArtifact }> = ({ report }) => (
  <Box>
    <Typography variant="h6" gutterBottom>{report.testName}</Typography>
    <Typography variant="body2" paragraph>
      <strong>Methodology:</strong> {report.methodology}
    </Typography>
    
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Participants</Typography>
      <Typography variant="body2">Count: {report.participants.count}</Typography>
      <Typography variant="body2">Demographics: {report.participants.demographics}</Typography>
      <Typography variant="body2">Criteria: {report.participants.recruitmentCriteria}</Typography>
    </Paper>
    
    <Typography variant="subtitle2" gutterBottom>Key Findings</Typography>
    {report.findings.map((finding) => (
      <Card key={finding.id} sx={{ mb: 1 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Chip
              label={finding.severity}
              size="small"
              color={
                finding.severity === 'critical' ? 'error' :
                finding.severity === 'high' ? 'warning' :
                finding.severity === 'medium' ? 'info' : 'default'
              }
            />
            <Typography variant="body2" sx={{ ml: 1 }}>
              Frequency: {finding.frequency}
            </Typography>
          </Box>
          <Typography variant="body2" paragraph>{finding.issue}</Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Impact:</strong> {finding.impact}
          </Typography>
          <Typography variant="body2" color="primary">
            <strong>Recommendation:</strong> {finding.recommendation}
          </Typography>
        </CardContent>
      </Card>
    ))}
    
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Metrics</Typography>
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <Typography variant="body2">Task Completion</Typography>
          <Typography variant="h6">{report.metrics.taskCompletionRate}%</Typography>
        </Grid>
        <Grid item xs={6} md={3}>
          <Typography variant="body2">Error Rate</Typography>
          <Typography variant="h6">{report.metrics.errorRate}%</Typography>
        </Grid>
        <Grid item xs={6} md={3}>
          <Typography variant="body2">Time on Task</Typography>
          <Typography variant="h6">{report.metrics.timeOnTask}s</Typography>
        </Grid>
        <Grid item xs={6} md={3}>
          <Typography variant="body2">Satisfaction</Typography>
          <Typography variant="h6">{report.metrics.satisfactionScore}/5</Typography>
        </Grid>
      </Grid>
    </Paper>
  </Box>
);

export default UXDesigner;